import { Box, DrawerBody, DrawerHeader, Button, SimpleGrid, IconButton, Icon, Flex } from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { IconClose } from 'assets/icons';
import { DrawerFixedButton } from 'components/Drawer';
import { FormikInput, FormikSelect } from 'components/Formik';
import { monthOptions, yearOptions } from 'containers/AccountBilling/shared/options';
import { useAddPaymentMethod } from 'hooks/accountBilling/useAddPaymentMethod';

import { initialPaymentValue, paymentFormValidationSchema } from './schema';
interface ICreateMessageProps {
  onClose: () => void;
}

const AddPaymentMethod: React.FC<ICreateMessageProps> = ({ onClose }) => {
  const { t } = useTranslation(['common', 'accountBilling']);

  const { status, mutateAsync } = useAddPaymentMethod();

  const formatCreditCardNum = (ccNum: string): number => {
    return +ccNum.replaceAll('-', '');
  };

  const creditCardFormat = (
    value: any,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void,
    name: string
  ) => {
    const v = value
      .replace(/\s+/g, '')
      .replace(/[^0-9]/gi, '')
      .substr(0, 16);
    const parts = [];

    for (let i = 0; i < v.length; i += 4) {
      parts.push(v.substr(i, 4));
    }

    setFieldValue(name, parts.length > 1 ? parts.join('-') : value);
  };

  const handleFormSubmit = (values: typeof initialPaymentValue) => {
    const postData = {
      number: formatCreditCardNum(values.number),
      card_name: values.card_name,
      expiry_month: values.expiry_month.value,
      expiry_year: values.expiry_year.value,
      cvc: values.cvc,
    };
    mutateAsync(postData).then(() => {
      if (onClose) {
        onClose();
      }
    });
  };

  return (
    <Box>
      <DrawerHeader>
        <Flex justifyContent="space-between" alignItems="center">
          {t('accountBilling:addPaymentMethod')}
          <IconButton
            aria-label="Close"
            icon={<Icon as={IconClose} height="15px" width="15px" />}
            onClick={onClose}
            variant="unstyled"
          />
        </Flex>
      </DrawerHeader>
      <DrawerBody style={{ overflow: 'unset' }}>
        <Formik
          enableReinitialize
          initialValues={initialPaymentValue}
          validationSchema={paymentFormValidationSchema}
          onSubmit={handleFormSubmit}
        >
          {({ setFieldValue }) => (
            <Form noValidate>
              <FormikInput
                type="text"
                name="number"
                label={t('accountBilling:cardNumber')}
                inputProps={{
                  placeholder: 'xxxx-xxxx-xxxx-xxxx',
                }}
                formControlProps={{ isRequired: true }}
                labelProps={{
                  fontWeight: 300,
                  color: 'newGray.1000',
                }}
                afterChangeCallback={e => creditCardFormat(e.target.value, setFieldValue, 'number')}
              />
              <FormikInput
                type="text"
                name="card_name"
                label={t('accountBilling:nameOnCard')}
                inputProps={{
                  placeholder: t('accountBilling:enterCardHoldersName'),
                }}
                formControlProps={{ isRequired: true }}
              />
              <SimpleGrid columns={2} spacing="2">
                <FormikSelect
                  name="expiry_month"
                  options={monthOptions}
                  label={t('accountBilling:expiryMonth')}
                  selectProps={{
                    placeholder: t('accountBilling:selectMonth'),
                    isClearable: false,
                  }}
                />
                <FormikSelect
                  name="expiry_year"
                  options={yearOptions}
                  label={t('accountBilling:expiryYear')}
                  selectProps={{
                    placeholder: t('accountBilling:selectYear'),
                    isClearable: false,
                  }}
                />
              </SimpleGrid>
              <FormikInput
                type="text"
                name="cvc"
                label={t('accountBilling:cvvCode')}
                inputProps={{
                  placeholder: t('accountBilling:enterCVV'),
                }}
              />
              <DrawerFixedButton>
                <Flex alignItems="center" w="full" gridGap={4}>
                  <Button variant="ghost" colorScheme="secondary" w="full" onClick={onClose}>
                    {t('common:cancel')}
                  </Button>
                  <Button colorScheme="secondary" type="submit" w="full" isLoading={status === 'loading'}>
                    {t('accountBilling:addCard')}
                  </Button>
                </Flex>
              </DrawerFixedButton>
            </Form>
          )}
        </Formik>
      </DrawerBody>
    </Box>
  );
};
export default AddPaymentMethod;
